import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { usePlayAction } from '../../hooks/usePlayAction';

export type AppNavPage = 'home' | 'dashboard' | 'admin' | 'game' | 'history';

interface AppNavProps {
  /** Which page we're on — contextual links are hidden to avoid self-links */
  currentPage?: AppNavPage;
}

export const AppNav: React.FC<AppNavProps> = ({ currentPage }) => {
  const { t }             = useTranslation();
  const { token, logout } = useAuth();
  const navigate          = useNavigate();
  const isAdmin           = useSelector((s: RootState) => s.auth.isAdmin);
  const [open, setOpen]   = useState(false);
  const navRef            = useRef<HTMLDivElement>(null);
  const { play, loading: playLoading } = usePlayAction();

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close dropdown on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const close = () => setOpen(false);

  const handlePlay = async () => {
    close();
    await play();
  };

  const handleLogout = () => {
    close();
    logout();
    navigate('/');
  };

  return (
    <div className="app-nav" ref={navRef}>
      {/* Hamburger — visible only on mobile */}
      <button
        className="btn-transparent app-nav__hamburger"
        onClick={() => setOpen(v => !v)}
        aria-label="Menu"
        aria-expanded={open}
      >
        {open ? '✕' : '☰'}
      </button>

      {/* Nav links */}
      <div className={`app-nav__menu${open ? ' app-nav__menu--open' : ''}`}>
        {token ? (
          <>
            {/* PLAY — hidden on home and game (dedicated buttons exist there) */}
            {currentPage !== 'home' && currentPage !== 'game' && (
              <button
                className="btn-transparent app-nav__play"
                onClick={handlePlay}
                disabled={playLoading}
              >
                {playLoading ? t('common.loading') : t('common.navPlay')}
              </button>
            )}

            {currentPage !== 'home' && (
              <Link to="/" onClick={close}>
                <button className="btn-transparent">{t('common.backToMap')}</button>
              </Link>
            )}

            {currentPage !== 'dashboard' && (
              <Link to="/dashboard" onClick={close}>
                <button className="btn-transparent">{t('common.navDashboard')}</button>
              </Link>
            )}

            {currentPage !== 'history' && (
              <Link to="/history" onClick={close}>
                <button className="btn-transparent">{t('home.history')}</button>
              </Link>
            )}

            <Link to="/about" onClick={close}>
              <button className="btn-transparent">{t('about.aboutBtn')}</button>
            </Link>

            {isAdmin && currentPage !== 'admin' && (
              <Link to="/admin" onClick={close}>
                <button className="btn-transparent">{t('common.navAdmin')}</button>
              </Link>
            )}

            <LanguageSwitcher />

            <button className="btn-transparent" onClick={handleLogout}>
              {t('common.navLogout')}
            </button>
          </>
        ) : (
          <>
            <Link to="/login" onClick={close}>
              <button className="btn-transparent">{t('home.signIn')}</button>
            </Link>

            <Link to="/register" onClick={close}>
              <button className="btn-transparent">{t('home.register')}</button>
            </Link>

            <Link to="/history" onClick={close}>
              <button className="btn-transparent">{t('home.history')}</button>
            </Link>

            <Link to="/about" onClick={close}>
              <button className="btn-transparent">{t('about.aboutBtn')}</button>
            </Link>

            <LanguageSwitcher />
          </>
        )}
      </div>
    </div>
  );
};
